/**
 * reportRowDeleteResult
 *
 * The one sentence a row delete leaves behind, for every path that had no
 * confirmation left to carry it once delete became undoable (SYD-2447 §4.4):
 * `useRowDeleteTargets`, `ArTableBody`'s ⌘⌫ handler, `ArBulkActionsMenu` and a
 * bundle header's Delete. One function, so a skip is never described four ways.
 *
 * Reports the count, never the ids: `skippedIds` truncates at `AR_TABLE_PAGE_SIZE`
 * under the enumerated scopes while `skippedCount` stays exact, so a list read back
 * to the user can be shorter than the truth.
 *
 * `targets` is what the delete named, and it decides what the sentence may claim.
 * A named id list is testable, so whether any target was in the `drv~` namespace
 * picks the wording — the server returns one `skipped` for both reasons, and
 * calling every skip "locked" is a false claim the moment a managed row is in the
 * set. `{ allRows }` and `{ bundle }` name nothing: the server enumerates those
 * scopes and refuses rows the client never saw, so the sentence carries both
 * reasons rather than guessing between them.
 *
 * The namespace, never a live projection — `arTableRowOperations` refuses on the
 * namespace alone, so a row whose derived value was deleted minutes ago is still
 * refused while the repair drains it.
 *
 * A `failed` result is silent: the action already raised the reason through
 * `handleFirestoreWriteError`, and a "Deleted 0 rows." beside it would contradict it.
 */

import { MessageInstance } from 'antd/es/message/interface';
import { formDeriveCopy } from '@/services/formDerives';
import { formDeriveIdOfRowId } from '#types';
import type { ArRowId } from '#types/System.Artifacts.types';
import type { BulkActionResult } from './useArTableActions';

/**
 * The ids the user named, or the scope the server enumerated on their behalf.
 * `bundle` carries the bundle's own label ('Kit', 'Group') because that is the
 * word the sentence needs; its id says nothing a reader can use.
 */
type RowDeleteTargets =
  | readonly ArRowId[]
  | { allRows: true }
  | { bundle: string };

const reportRowDeleteResult = (
  messageApi: MessageInstance,
  result: BulkActionResult,
  targets: RowDeleteTargets,
): void => {
  if (result.failed) return;

  const deleted = `Deleted ${result.affected} ${result.affected === 1 ? 'row' : 'rows'}.`;
  if (result.skipped === 0) {
    messageApi.success(deleted);
    return;
  }

  if (Array.isArray(targets)) {
    const anyPlatformOwned = targets.some((id) => formDeriveIdOfRowId(id) !== null);
    const noun = result.skipped === 1 ? 'row' : 'rows';
    messageApi.warning(
      anyPlatformOwned
        ? `${deleted} ${formDeriveCopy.managedRowsSkipped(result.skipped)}`
        : `${deleted} ${result.skipped} locked ${noun} skipped.`,
    );
    return;
  }

  const refused = result.skipped === 1
    ? '1 row was skipped because it is locked or managed by a derived value.'
    : `${result.skipped} rows were skipped because they are locked or managed by a derived value.`;
  /** A refused row keeps its header, so the bundle the user aimed at is still there. */
  const remains = 'bundle' in targets ? ` The ${targets.bundle.toLowerCase()} remains.` : '';
  messageApi.warning(`${deleted} ${refused}${remains}`);
};

export { reportRowDeleteResult };
